import {
  cosApi,
  createModuleApiService,
  eduCenterApi,
  exmApi,
  finApi,
  infraApi,
  lrnApi,
  regApi,
  systemApi
} from '@/service/modules/api-service-config'
import { message } from '@/utils/naive-instance'
import { ref } from 'vue'

// 类型定义增强
interface ApiResponse<T = any> {
  data: T
  code: number
  msg: string
}
interface PageResponse<T = any> {
  list: T[]
  total: number
}
interface Pagination {
  pageNo: number
  pageSize: number
  total: number
}
type ModuleApiType = ReturnType<typeof createModuleApiService>
type ModuleApiMapKey =
  | 'app-api/system'
  | 'app-api/educ'
  | 'app-api/reg'
  | 'app-api/lrn'
  | 'app-api/exm'
  | 'app-api/fin'
  | 'app-api/infra'
  | 'app-api/cos'

// 模块映射表
const moduleApiMap: Record<ModuleApiMapKey, ModuleApiType> = {
  'app-api/system': systemApi,
  'app-api/educ': eduCenterApi,
  'app-api/reg': regApi,
  'app-api/lrn': lrnApi,
  'app-api/exm': exmApi,
  'app-api/fin': finApi,
  'app-api/infra': infraApi,
  'app-api/cos': cosApi
}

/**
 * 通用业务Store组合式函数（支持按key存储/取值）
 * @param moduleName 模块名
 */
export function useBaseBusinessStore(moduleName: ModuleApiMapKey) {
  // 1. 获取模块API实例
  const moduleApi =
    moduleApiMap[moduleName] || createModuleApiService(moduleName)

  // 2. 通用状态改造：按key存储（核心！）
  // 列表数据（分页）：{ key: { list: T[], pagination: Pagination } }
  const listStore = ref<
    Record<string, { list: any[]; pagination: Pagination }>
  >({})
  // 详情数据：{ key: T }
  const detailStore = ref<Record<string, any>>({})
  // 简易列表数据：{ key: T[] }
  const simpleListStore = ref<Record<string, any[]>>({})
  // 加载状态：{ key: boolean }
  const loadingStore = ref<Record<string, boolean>>({})

  // 3. 辅助方法：获取模块名
  const getModuleName = () => {
    return (
      Object.entries(moduleApiMap).find(
        (entry) => entry[1] === moduleApi
      )?.[0] || '未知模块'
    )
  }

  const resolveConfigAndKey = (configOrKey?: any, key?: string) => {
    if (typeof configOrKey === 'string') {
      return {
        config: undefined,
        key: configOrKey
      }
    }

    return {
      config: configOrKey,
      key
    }
  }

  // 4. 通用基础请求方法（按key控制loading）
  const getRequest = async <R = any>(
    apiPath: string,
    params?: any,
    config?: any,
    key?: string // 新增：唯一key，用于标记loading
  ): Promise<ApiResponse<R>> => {
    if (key) loadingStore.value[key] = true
    try {
      const res = await moduleApi.get(apiPath, params, config)
      return res as ApiResponse<R>
    } catch (error) {
      console.error(`[${getModuleName()}] GET ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  const postRequest = async <R = any>(
    apiPath: string,
    data?: any,
    config?: any,
    key?: string
  ): Promise<ApiResponse<R>> => {
    if (key) loadingStore.value[key] = true
    try {
      const showSuccessMessage = Boolean(config?.showSuccessMessage)
      const successMessage = config?.successMessage
      const requestConfig =
        config && typeof config === 'object' ? { ...config } : config

      if (requestConfig && typeof requestConfig === 'object') {
        delete (requestConfig as Record<string, any>).showSuccessMessage
        delete (requestConfig as Record<string, any>).successMessage
        delete (requestConfig as Record<string, any>).suppressSuccessMessage
      }

      const res = await moduleApi.post(apiPath, data, requestConfig)
      if (res?.code === 0) {
        if (showSuccessMessage) {
          message.success(successMessage || res.msg || '成功')
        }
      } else {
        message.error(res?.msg || '失败')
      }
      return res as ApiResponse<R>
    } catch (error) {
      console.error(`[${getModuleName()}] POST ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  const deleteRequest = async <R = any>(
    apiPath: string,
    params?: any,
    config?: any,
    key?: string
  ): Promise<ApiResponse<R>> => {
    if (key) loadingStore.value[key] = true
    try {
      const res = await moduleApi.delete(apiPath, params, config)
      return res as ApiResponse<R>
    } catch (error) {
      console.error(`[${getModuleName()}] DELETE ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  const putRequest = async <R = any>(
    apiPath: string,
    data?: any,
    config?: any,
    key?: string
  ): Promise<ApiResponse<R>> => {
    if (key) loadingStore.value[key] = true
    try {
      const res = await moduleApi.put(apiPath, data, config)
      return res as ApiResponse<R>
    } catch (error) {
      console.error(`[${getModuleName()}] PUT ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  const patchRequest = async <R = any>(
    apiPath: string,
    data?: any,
    config?: any,
    key?: string
  ): Promise<ApiResponse<R>> => {
    if (key) loadingStore.value[key] = true
    try {
      const res = await moduleApi.patch(apiPath, data, config)
      return res as ApiResponse<R>
    } catch (error) {
      console.error(`[${getModuleName()}] PATCH ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  // 5. 通用业务方法改造（按key存储数据）
  /**
   * 获取分页列表
   * @param apiPath 接口路径
   * @param params 请求参数
   * @param key 唯一标识（用于存储数据）
   */
  const fetchData = async <T = any>(
    apiPath: string,
    params: any,
    key: string
  ) => {
    if (key) loadingStore.value[key] = true
    try {
      const res = await getRequest<PageResponse<T>>(
        apiPath,
        params,
        undefined,
        key
      )
      if (!res.data) {
        throw new Error('接口返回数据格式异常')
      }
      // 按key存储列表+分页
      listStore.value[key] = {
        list: res.data.list || res.data || [],
        pagination: {
          pageNo: params.pageNo || 1,
          pageSize: params.pageSize || 10,
          total: res.data.total || 0
        }
      }
      return res.data
    } catch (error) {
      console.error(`[${getModuleName()}] PATCH ${apiPath} 失败`, error)
      throw error
    } finally {
      if (key) loadingStore.value[key] = false
    }
  }

  /**
   * 获取详情
   * @param apiPath 接口路径
   * @param params 请求参数
   * @param key 唯一标识
   */
  const getDetail = async <T = any>(
    apiPath: string,
    params: any,
    configOrKey?: any,
    key?: string
  ) => {
    const { config, key: resolvedKey } = resolveConfigAndKey(configOrKey, key)
    const res = await getRequest<T>(apiPath, params, config, resolvedKey)
    if (resolvedKey) {
      detailStore.value[resolvedKey] = res.data
    }
    return res.data
  }

  /**
   * 获取简易列表（核心改造！）
   * @param apiPath 接口路径
   * @param params 请求参数
   * @param key 唯一标识（比如'myCourse'/'courseType'）
   */
  const fetchSimpleList = async <T = any>(
    apiPath: string,
    params?: any,
    configOrKey?: any,
    key?: string
  ) => {
    const { config, key: resolvedKey } = resolveConfigAndKey(configOrKey, key)
    const res = await getRequest<T[]>(apiPath, params, config, resolvedKey)
    if (resolvedKey) {
      simpleListStore.value[resolvedKey] = res.data || []
    }
    return res.data
  }

  /**
   * 更新分页信息
   * @param key 唯一标识
   * @param pageNo 页码
   * @param pageSize 页大小
   */
  const updatePagination = (key: string, pageNo: number, pageSize: number) => {
    if (listStore.value[key]) {
      listStore.value[key].pagination.pageNo = pageNo
      listStore.value[key].pagination.pageSize = pageSize
    }
  }

  // 6. 取值方法（按key获取数据，提供默认值）
  /**
   * 获取简易列表
   * @param key 唯一标识
   * @returns 对应key的列表数据
   */
  const getSimpleList = <T = any>(key: string): T[] => {
    const store = simpleListStore.value || {}
    const data = store[key] ?? []
    console.log(
      `getSimpleList 取值 key=${key}，原始值:`,
      store[key],
      '返回值:',
      data
    )
    return data as T[]
  }

  /**
   * 获取分页列表
   * @param key 唯一标识
   * @returns 列表+分页
   */
  const getList = <T = any>(key: string) => {
    return (
      listStore.value[key] || {
        list: [] as T[],
        pagination: { pageNo: 1, pageSize: 10, total: 0 }
      }
    )
  }

  /**
   * 获取详情
   * @param key 唯一标识
   * @returns 对应key的详情数据
   */
  const getDetailData = <T = any>(key: string): T => {
    const store = detailStore.value || {}
    const data = store[key] ?? {}
    console.log(
      `getDetailData 取值 key=${key}，原始值:`,
      store[key],
      '返回值:',
      data
    )
    return data as T
  }

  /**
   * 获取加载状态
   * @param key 唯一标识
   * @returns 加载状态
   */
  const getLoading = (key: string): boolean => {
    return loadingStore.value[key] || false
  }

  // 7. 清空数据方法（可选）
  const clearSimpleList = (key: string) => {
    delete simpleListStore.value[key]
  }

  const clearList = (key: string) => {
    delete listStore.value[key]
  }

  const clearDetail = (key: string) => {
    delete detailStore.value[key]
  }

  // 返回所有方法和状态（状态用ref包裹，保持响应式）
  return {
    // 存储容器（建议仅内部使用，外部用get方法取值）
    listStore,
    detailStore,
    simpleListStore,
    loadingStore,
    // 基础请求方法
    getRequest,
    postRequest,
    deleteRequest,
    putRequest,
    patchRequest,
    // 业务方法
    fetchData,
    getDetail,
    fetchSimpleList,
    updatePagination,
    // 取值方法（核心：按key取值）
    getSimpleList,
    getList,
    getDetailData,
    getLoading,
    // 清空方法
    clearSimpleList,
    clearList,
    clearDetail
  }
}
